/**
 * Impressão de Tickets Individuais por Produto
 *
 * Quando um produto tem print_config.ticket_enabled = true,
 * imprime N tickets individuais (1 por unidade vendida).
 * Exemplo: 10 bebidas = 10 tickets separados.
 * Produto por quilo/litro imprime 1 ticket pela linha (0,350 kg é 1 item).
 */
import { medidaDoProduto } from "../lib/unidadeVenda";
import { smartPrint } from "./printConnector";
import { getTenantSlug } from "./tenantScope";
import { getImpressaoConfig, buildFontScaleStyle } from "./cupomFormatters";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
let _productsCache = null;
let _productsCacheTime = 0;
let _productsCacheSlug = null;
const CACHE_TTL = 120000; // 2 min

async function getProducts() {
  const slug = getTenantSlug();
  if (_productsCacheSlug && _productsCacheSlug !== slug) {
    _productsCache = null;
    _productsCacheTime = 0;
  }
  _productsCacheSlug = slug;
  const now = Date.now();
  if (_productsCache && (now - _productsCacheTime) < CACHE_TTL) return _productsCache;
  try {
    const tok = localStorage.getItem("token");
    const res = await fetch(`${API_BASE}/api/products`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      signal: AbortSignal.timeout(10000),
    });
    if (res.ok) {
      _productsCache = await res.json();
      _productsCacheTime = now;
      return _productsCache;
    }
  } catch (_) {}
  return _productsCache || [];
}

function gerarTicketHTML({ title, pedidoCodigo, sequencia, data, clienteNome, empresa, fontScaleCss }) {
  const nomeEmpresa = empresa?.nome || empresa?.company_name || "Núcleo";
  const logoUrl = empresa?.logo_url || "";

  // Mesma altura/largura do cupom CAIXA principal (97px) — logo mais visivel.
  const logoSide = 96;
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" alt="Logo" style="width:${logoSide}px;height:${logoSide}px;object-fit:cover;display:block;margin:0 auto 6px;" onerror="this.style.display='none';" />`
    : "";

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Ticket ${pedidoCodigo}</title>
  <style>
    @page { size: 80mm auto; margin: 3mm; }
    body { margin: 0; padding: 8px; font-family: "Courier New", monospace; font-size: 13px; font-weight: bold; background: none; color: #000; }
    .center { text-align: center; }
    @media print { body { max-width: 72.1mm; margin: 0 auto; } }
    ${fontScaleCss || ""}
  </style>
</head>
<body style="max-width:72.1mm;margin:0 auto;">
  ${logoHtml}
  <div class="center" style="font-size:14px;font-weight:bold;">${nomeEmpresa}</div>
  <div class="center" style="font-size:12px;margin:4px 0;border-bottom:1px dashed #000;padding-bottom:6px;">--- TICKET ---</div>
  <div class="center" style="font-size:20px;font-weight:bold;margin:8px 0;">${title}</div>
  <div class="center" style="font-size:13px;">Cód. #${pedidoCodigo} -> ${data}</div>
  <div class="center" style="font-size:32px;font-weight:bold;margin:8px 0;">${sequencia}</div>
  ${clienteNome ? `<div class="center" style="font-size:13px;margin-top:4px;">Cliente: ${clienteNome}</div>` : ""}
  <div class="center" style="font-size:12px;margin-top:6px;border-top:1px dashed #000;padding-top:6px;">--------------</div>
</body>
</html>`;
}

/**
 * Imprime tickets individuais para itens do pedido que têm ticket habilitado.
 * Busca automaticamente a lista de produtos (com cache) para verificar print_config.
 * @param {Object} pedido - Pedido com items, codigo, cliente_nome
 * @param {Object} empresa - Dados da empresa (nome, logo_url)
 * @param {Array} [allProducts] - Opcional: lista de produtos (evita fetch extra)
 */
export async function imprimirTickets(pedido, empresa, allProducts) {
  if (!pedido?.items?.length) return;

  const products = allProducts?.length ? allProducts : await getProducts();
  if (!products?.length) return;

  // Verificar se algum item tem ticket habilitado (fast check)
  const hasAnyTicket = products.some(p => {
    let cfg = {};
    try { cfg = typeof p.print_config === "string" ? JSON.parse(p.print_config || "{}") : (p.print_config || {}); } catch (_) {}
    return cfg.ticket_enabled;
  });
  if (!hasAnyTicket) return;

  const codigoStr = String((pedido.codigo ?? "").replace?.(/^#+/, "") || pedido.codigo || "").padStart(5, "0");
  const dataStr = new Date().toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  // font_scale aplicado tambem nos tickets individuais
  const impressaoConfig = await getImpressaoConfig();
  const fontScaleCss = buildFontScaleStyle(impressaoConfig.font_scale);

  // Helper: print ticket for a product
  const printTicketForProduct = async (productId, itemQty, itemName) => {
    if (!productId) return;
    const product = products.find(p => String(p.id) === String(productId));
    if (!product) return;

    let config = {};
    try { config = typeof product.print_config === "string" ? JSON.parse(product.print_config || "{}") : (product.print_config || {}); } catch (_) {}

    if (!config.ticket_enabled) return;

    // Totem segue a config do Balcão (mesmos produtos, mesmos tickets)
    const moduloPedido = pedido.modulo === "Totem" ? "Balcao" : (pedido.modulo || "");
    const modulosPermitidos = config.ticket_modules || ["Balcao", "Delivery", "Mesas", "Comandas", "Cardapio"];
    if (moduloPedido && !modulosPermitidos.includes(moduloPedido)) return;

    const title = config.ticket_title || itemName || product.name || "TICKET";
    const route = config.ticket_route || "CAIXA";
    // Quilo/litro (30/09/2026): 1 ticket pela linha — 0,350 kg não imprimia
    // nenhum (o laço ia de 1 até 0,35) e 1,5 kg imprimia "1/1.5"
    const totalTickets = medidaDoProduto(product) ? 1 : itemQty;

    for (let i = 1; i <= totalTickets; i++) {
      const html = gerarTicketHTML({
        title,
        pedidoCodigo: codigoStr,
        sequencia: `${i}/${totalTickets}`,
        data: dataStr,
        clienteNome: pedido.cliente_nome || "",
        empresa,
        fontScaleCss,
      });
      await smartPrint(html, route, [], null, empresa, moduloPedido);
    }
  };

  for (const item of pedido.items.filter(it => !it.deleted)) {
    const qty = item.quantidade || item.quantity || 1;
    // Check top-level item
    await printTicketForProduct(item.product_id || item.id, qty, item.nome || item.name);
  }
}
